import { useEffect, useRef, useState } from "react";
import { fetchInvoiceMailAttachment } from "@/lib/invoiceMail";
import type { InvoiceApprovalPreview } from "@/lib/invoiceApproval";

let activeRenders = 0;
const waiters: Array<() => void> = [];

function acquireRenderSlot(): Promise<void> {
  if (activeRenders < 3) {
    activeRenders += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    waiters.push(() => {
      activeRenders += 1;
      resolve();
    });
  });
}

function releaseRenderSlot(): void {
  activeRenders = Math.max(0, activeRenders - 1);
  const next = waiters.shift();
  if (next) next();
}

export function InvoiceThumb({
  candidateId,
  preview,
  label,
}: {
  candidateId: number;
  preview?: InvoiceApprovalPreview | null;
  label: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!preview?.id) return;
    let cancelled = false;
    let revoked: string | null = null;
    const controller = new AbortController();
    setFailed(false);
    setImageUrl(null);

    void (async () => {
      await acquireRenderSlot();
      try {
        const blob = await fetchInvoiceMailAttachment(candidateId, preview.id, controller.signal);
        if (cancelled) return;
        if (preview.kind === "image") {
          revoked = URL.createObjectURL(blob);
          setImageUrl(revoked);
          return;
        }
        const pdfjs = await import("pdfjs-dist");
        const workerSrc = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
        pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
        const data = new Uint8Array(await blob.arrayBuffer());
        const pdf = await pdfjs.getDocument({ data }).promise;
        try {
          const page = await pdf.getPage(1);
          const canvas = canvasRef.current;
          if (!canvas || cancelled) return;
          const unscaled = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: 360 / unscaled.width });
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          const ctx = canvas.getContext("2d");
          if (!ctx) return;
          await page.render({ canvasContext: ctx, viewport }).promise;
        } finally {
          await pdf.destroy();
        }
      } catch (err) {
        if (cancelled || (err instanceof Error && err.name === "AbortError")) return;
        setFailed(true);
      } finally {
        releaseRenderSlot();
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [candidateId, preview?.id, preview?.kind]);

  if (!preview || failed) {
    return (
      <div className="flex h-full items-center justify-center bg-fill-tertiary/40 px-4 text-center text-xs text-label-tertiary">
        {failed ? "Vorschau nicht verfügbar" : "Kein Belegbild"}
      </div>
    );
  }

  if (preview.kind === "image") {
    if (!imageUrl) {
      return <div className="h-full animate-pulse bg-fill-tertiary/50" />;
    }
    return <img src={imageUrl} alt={label} className="h-full w-full object-cover object-top" />;
  }

  return <canvas ref={canvasRef} className="h-full w-full object-cover object-top" aria-label={label} />;
}
